/**
 * E06-F06-T04 — «Похожие списки» hint under the Targeting block of the Intake form
 * (docs/superpowers/specs/2026-10-15-similar-research-lists-design.md
 * #подсказка-похожие-списки-в-заявке).
 *
 *   - Recalculated ~0.5 s after Industry / Sub-categories / Locations change
 *     (including a brief parse filling them); hidden while all three are empty.
 *   - Scoring is server-side (POST /research/list/similar, E06-F06-T02): ≤5 rows,
 *     score ≥ 0.3, the edited request excluded, empty drafts never suggested.
 *   - Card: title, linked project, date, companies / approved, match chips.
 *   - Actions: «Open» (go to the request) and «Use criteria» (the host fills the form
 *     via the existing Clone, asking first when the form is not blank).
 *   - A failing hint request renders NOTHING — the form stays fully usable.
 */
import { useEffect, useState, type JSX } from 'react';
import type {
  IntakeCriteria,
  ResearchSearchMatchField,
  SimilarListRow,
  SimilarListsRequest,
} from '@eer/shared';
import { useSimilarLists } from './researchLibraryApi.js';
import { formatDate } from './researchLibraryModel.js';
import {
  SIMILAR_LISTS_DEBOUNCE_MS,
  hasSimilarListsTrigger,
  similarListsInput,
} from './intakeModel.js';

/** Display label of each matched criteria field (server sends the field key). */
const MATCH_LABELS: Record<ResearchSearchMatchField, string> = {
  title: 'title',
  client: 'client',
  industry: 'industry',
  subcategory: 'sub-category',
  location: 'location',
  competitor: 'competitor',
  company_type: 'company type',
  brief: 'brief',
};

/** `value` delayed by `ms`, keyed by its JSON so a fresh-but-equal object never re-fires. */
function useDebouncedInput(value: SimilarListsRequest, ms: number): SimilarListsRequest {
  const key = JSON.stringify(value);
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(JSON.parse(key) as SimilarListsRequest), ms);
    return () => clearTimeout(t);
  }, [key, ms]);
  return debounced;
}

export interface SimilarListsPanelProps {
  /** The form's live criteria. */
  readonly criteria: IntakeCriteria;
  /** The request being edited (an edit of an existing one) — never suggested. */
  readonly excludeRequestId?: number | null;
  /** «Open» — navigate to the found request. Omitted → the button is not shown. */
  readonly onOpen?: (row: SimilarListRow) => void;
  /** «Use criteria» — fill the form with the found list's criteria. */
  readonly onTakeCriteria: (row: SimilarListRow) => void;
  /** The request whose criteria are being taken right now (button busy). */
  readonly takingRequestId?: number | null;
}

export function SimilarListsPanel({
  criteria,
  excludeRequestId = null,
  onOpen,
  onTakeCriteria,
  takingRequestId = null,
}: SimilarListsPanelProps): JSX.Element | null {
  const triggered = hasSimilarListsTrigger(criteria);
  const input = useDebouncedInput(
    similarListsInput(criteria, excludeRequestId),
    SIMILAR_LISTS_DEBOUNCE_MS,
  );
  const debouncedTriggered =
    input.industry !== '' || input.subCategories.length > 0 || input.locations.length > 0;
  const query = useSimilarLists(input, triggered && debouncedTriggered);

  if (!triggered || !debouncedTriggered || query.isError) return null;
  const rows = query.data?.rows ?? [];
  if (rows.length === 0) return null;

  return (
    <section
      data-testid="intake-similar-lists"
      className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 flex flex-col gap-3"
    >
      <h3 className="text-sm font-semibold text-[var(--color-text-strong)]">
        <i className="fa-solid fa-layer-group mr-1 text-[var(--color-accent)]" aria-hidden />
        Similar lists
      </h3>
      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <li
            key={row.requestId}
            data-testid="similar-list-card"
            data-request-id={row.requestId}
            className="rounded-md border border-[var(--color-border)] bg-white p-3 flex flex-wrap items-start justify-between gap-3"
          >
            <div className="min-w-0 flex-1">
              {/* E06-F06-T05: wrap to ≤2 lines (no single-line ellipsis); full name on hover. */}
              <div
                className="font-medium text-sm text-slate-800 line-clamp-2 break-words"
                data-testid="similar-list-title"
                title={row.title || `Request #${row.requestId}`}
              >
                {row.title || `Request #${row.requestId}`}
              </div>
              <div className="text-xs text-slate-500" data-testid="similar-list-meta">
                {row.projectLabel ?? <span className="italic">Standalone</span>} ·{' '}
                {formatDate(row.createdAt)} ·{' '}
                <span data-testid="similar-list-companies">{row.companies}</span> companies ·{' '}
                <span data-testid="similar-list-accepted">{row.accepted}</span> approved
              </div>
              {row.matches.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {row.matches.map((m, i) => (
                    <span
                      key={`${m.field}-${i}`}
                      data-testid="similar-list-match-chip"
                      data-field={m.field}
                      title={`${MATCH_LABELS[m.field] ?? m.field}: ${m.value}`}
                      className="max-w-[260px] truncate px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800"
                    >
                      <span className="text-amber-600">{MATCH_LABELS[m.field] ?? m.field}:</span>{' '}
                      {m.value}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 whitespace-nowrap">
              {onOpen && (
                <button
                  type="button"
                  data-testid="similar-list-open"
                  onClick={() => onOpen(row)}
                  className="text-xs px-2.5 py-1 border border-slate-200 rounded text-slate-600 hover:bg-slate-100"
                >
                  Open
                </button>
              )}
              <button
                type="button"
                data-testid="similar-list-take"
                disabled={takingRequestId != null}
                onClick={() => onTakeCriteria(row)}
                className="text-xs px-2.5 py-1 border border-slate-200 rounded text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                {takingRequestId === row.requestId ? 'Loading…' : 'Use criteria'}
              </button>
            </div>
          </li>
        ))}
      </ul>
      <p className="text-xs text-[var(--color-text-muted)]" data-testid="similar-lists-reuse-note">
        Companies already checked will be reused automatically.
      </p>
    </section>
  );
}

export default SimilarListsPanel;
